import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, EditorField } from '../@types/editor'

import {
  NUMBER_LOTTERY_FIELD_NAME,
  NUMBER_LOTTERY_PER_PLAY_ADD_ON_NAME,
} from '../constants/numberLottery'
import {
  isFieldComplete as isFieldCompleteBase,
  isPlayComplete as isPlayCompleteHelper,
  setFieldSelected,
} from '../utils/editorFields'
import { editorStorage } from '../utils/editorPersistence'
import { createPlayKey, hasDeletablePlays, pickNumbers } from '../utils/editorPlay'
import { createLotteryStoreCache, createPlaysState } from '../utils/editorStore'

export type EditorPlay = {
  fields: EditorField[]
  id: string
  quickPick?: string
  key: string
  perPlayAddOn: Maybe<{ name: string; value: string }>
}

const EXTRA_HIT_ADDON_VALUE = 'true'

const createPlay = (config: Config, index: number): EditorPlay => ({
  fields: config.numbers.map(({ name }) => ({ name, selected: [] })),
  id: `play-${index + 1}`,
  key: createPlayKey(),
  perPlayAddOn: null,
})

const isExtraHitConfigured = (config: Config) =>
  config.addOns.some(({ name }) => name === NUMBER_LOTTERY_PER_PLAY_ADD_ON_NAME.ExtraHit)

const isExtraHitEnabled = (play: EditorPlay) => !!play.perPlayAddOn?.value

const isExtraHitField = (play: EditorPlay, { name }: Pick<EditorField, 'name'>) =>
  name === NUMBER_LOTTERY_FIELD_NAME.Extra && isExtraHitEnabled(play)

const isFieldIgnored = (config: Config, play: EditorPlay, fieldIndex: number) =>
  !!config.numbers[fieldIndex] && isExtraHitField(play, config.numbers[fieldIndex])

const getIgnoredFieldNames = (config: Config, play: EditorPlay): string[] =>
  config.numbers.filter((_, index) => isFieldIgnored(config, play, index)).map(({ name }) => name)

const isFieldComplete = (config: Config, play: EditorPlay, fieldIndex: number) =>
  isFieldIgnored(config, play, fieldIndex) || isFieldCompleteBase(config, play, fieldIndex)

const isPlayComplete = (config: Config, play: EditorPlay) =>
  isPlayCompleteHelper(config, play, isFieldComplete)

const quickPickPlay = (config: Config, play: EditorPlay) => {
  const replaceAll = isPlayComplete(config, play)
  play.fields.forEach((field, fieldIndex) => {
    if (isFieldIgnored(config, play, fieldIndex)) return
    field.selected = pickNumbers(config.numbers[fieldIndex], replaceAll ? [] : field.selected)
  })
}

const reconcilePlay = (config: Config, play: EditorPlay) => {
  if (play.perPlayAddOn && !isExtraHitConfigured(config)) play.perPlayAddOn = null

  config.numbers.forEach(({ selectableNumbers }, fieldIndex) => {
    if (isFieldIgnored(config, play, fieldIndex)) {
      setFieldSelected(play, fieldIndex, [...selectableNumbers])
    }
  })
}

const createStore = (lotteryId: LotteryId) =>
  defineStore(
    `ltc_number_lottery_${lotteryId.toLowerCase()}`,
    () => {
      const playsState = createPlaysState({
        createPlay,
        isPlayComplete,
        isPlayDirty: isExtraHitEnabled,
        lotteryId,
        quickPickPlay,
        reconcilePlay,
      })
      const { getPlay, plays, removeEmptyPlaysExcept } = playsState

      const canDeleteAllPlays = (config: Config) =>
        hasDeletablePlays(plays.value, (play) => isPlayComplete(config, play))

      const clearPlay = (playId: string) => {
        const play = getPlay(playId)
        if (!play) return
        play.fields.forEach((field) => (field.selected = []))
        play.perPlayAddOn = null
        removeEmptyPlaysExcept(playId)
      }

      const isExtraHitAvailable = (config: Config) => isExtraHitConfigured(config)

      const remainingInField = (play: EditorPlay, fieldIndex: number, selectableMax: number) =>
        Math.max(0, selectableMax - play.fields[fieldIndex].selected.length)

      const setSelectedNumbers = (playId: string, fieldIndex: number, selected: number[]) =>
        setFieldSelected(getPlay(playId), fieldIndex, selected)

      const toggleExtraHit = (playId: string, config: Config) => {
        const play = getPlay(playId)
        if (!play) return

        if (isExtraHitEnabled(play)) {
          play.perPlayAddOn = null
          return
        }

        if (!isExtraHitAvailable(config)) return

        play.perPlayAddOn = {
          name: NUMBER_LOTTERY_PER_PLAY_ADD_ON_NAME.ExtraHit,
          value: EXTRA_HIT_ADDON_VALUE,
        }
      }

      return {
        ...playsState,
        canDeleteAllPlays,
        clearPlay,
        getIgnoredFieldNames,
        isExtraHitAvailable,
        isExtraHitEnabled,
        isFieldComplete,
        isPlayComplete,
        remainingInField,
        setSelectedNumbers,
        toggleExtraHit,
      }
    },
    {
      persist: {
        omit: ['expandedPlayId', 'pendingRemovals'],
        storage: editorStorage(),
      },
    },
  )

export const useNumberLotteryStore = createLotteryStoreCache(createStore)
